import React, { useEffect, useState } from "react";
import { updateLastActivity, clearSession } from "../services/authService";
import Navbar from "../components/NavBar";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";

import * as XLSX from "xlsx";

// ====== Helpers de formato y cálculo ======

const normalizeName = (name) => {
  if (!name) return "";
  return String(name)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();
};

const parseProgress = (value) => {
  if (value == null) return 0;
  const str = String(value).replace("%", "").replace(",", ".").trim();
  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
};

// Intenta parsear fechas del estilo: "miércoles, 26 de noviembre de 2025, 22:18"
const parseMoodleDate = (value) => {
  if (!value) return null;
  const str = String(value).toLowerCase();

  const months = {
    enero: 0,
    febrero: 1,
    marzo: 2,
    abril: 3,
    mayo: 4,
    junio: 5,
    julio: 6,
    agosto: 7,
    septiembre: 8,
    setiembre: 8,
    octubre: 9,
    noviembre: 10,
    diciembre: 11,
  };

  const regex =
    /(\d{1,2})\s+de\s+([a-zñ]+)\s+de\s+(\d{4}).*?(\d{1,2}):(\d{2})/i;
  const match = str.match(regex);
  if (!match) return null;

  const day = parseInt(match[1], 10);
  const monthName = match[2];
  const year = parseInt(match[3], 10);
  const hour = parseInt(match[4], 10);
  const minute = parseInt(match[5], 10);

  const monthIndex = months[monthName];
  if (monthIndex == null) return null;

  return new Date(year, monthIndex, day, hour, minute);
};

const diffDays = (dateA, dateB) => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const diff = dateA.getTime() - dateB.getTime();
  return Math.floor(diff / msPerDay);
};

const inferActivityType = (colName) => {
  const name = colName.toLowerCase();
  if (name.includes("diagnostica")) return "Diagnóstica";
  if (name.includes("formativa")) return "Formativa";
  if (name.includes("integradora")) return "Integradora";
  if (name.includes("producto final") || name.includes("producto"))
    return "Producto final";
  if (name.includes("autoevaluacion") || name.includes("autoevaluación"))
    return "Autoevaluación";
  if (name.includes("foro")) return "Foro";
  if (name.includes("tarea")) return "Tarea";
  return "Otra";
};

// Inferir unidad a partir del nombre de la actividad
const inferUnidadFromColName = (colName) => {
  const name = colName.toLowerCase();

  const regexU = /u\s*([1-9]\d*)/i; // U1, U 2, etc.
  const matchU = name.match(regexU);
  if (matchU) {
    return `Unidad ${matchU[1]}`;
  }

  const regexUnidad = /unidad\s+([1-9]\d*)/i; // Unidad 1, Unidad 2, etc.
  const matchUnidad = name.match(regexUnidad);
  if (matchUnidad) {
    return `Unidad ${matchUnidad[1]}`;
  }

  return "General";
};

const calculateRisk = ({
  totalCurso,
  progreso,
  diasDesdeUltimaConexion,
  actividadesNoEntregadas,
}) => {
  let nivel = "bajo";
  if (totalCurso < 60) {
    nivel = "alto";
  } else if (totalCurso < 70) {
    nivel = "medio";
  } else {
    nivel = "bajo";
  }

  if (progreso < 50) {
    if (nivel === "bajo") nivel = "medio";
    else if (nivel === "medio") nivel = "alto";
    else if (nivel === "alto") nivel = "muy_alto";
  }

  if (diasDesdeUltimaConexion != null) {
    if (diasDesdeUltimaConexion > 30) {
      nivel = "muy_alto";
    } else if (diasDesdeUltimaConexion > 14) {
      if (nivel === "bajo") nivel = "medio";
      else if (nivel === "medio") nivel = "alto";
      else if (nivel === "alto") nivel = "muy_alto";
    }
  }

  if (actividadesNoEntregadas > 3) {
    if (nivel === "bajo") nivel = "medio";
    else if (nivel === "medio") nivel = "alto";
    else if (nivel === "alto") nivel = "muy_alto";
  }

  return nivel;
};

const riskToLabel = (risk) => {
  switch (risk) {
    case "muy_alto":
      return "Muy alto";
    case "alto":
      return "Alto";
    case "medio":
      return "Medio";
    case "bajo":
    default:
      return "Bajo";
  }
};

const riskToSeverity = (risk) => {
  switch (risk) {
    case "muy_alto":
      return "danger";
    case "alto":
      return "warning";
    case "medio":
      return "info";
    case "bajo":
    default:
      return "success";
  }
};

// ==================== COMPONENTE ====================

export default function Limpiador() {
  const [progresoData, setProgresoData] = useState([]);
  const [calificacionesData, setCalificacionesData] = useState([]);
  const [alumnos, setAlumnos] = useState([]);
  const [selectedAlumno, setSelectedAlumno] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ==== Lógica de sesión / inactividad ====
  useEffect(() => {
    updateLastActivity();

    const timer = setInterval(() => {
      const now = Date.now();
      const last = parseInt(localStorage.getItem("lastActivity") || "0", 10);

      if (now - last > 15 * 60 * 1000) {
        clearSession();
        window.location.href = "/login";
      }
    }, 60 * 1000);

    const update = () => updateLastActivity();

    window.addEventListener("mousemove", update);
    window.addEventListener("keydown", update);
    window.addEventListener("beforeunload", clearSession);

    return () => {
      clearInterval(timer);
      window.removeEventListener("mousemove", update);
      window.removeEventListener("keydown", update);
      window.removeEventListener("beforeunload", clearSession);
    };
  }, []);

  // ==== Lectura de archivos Excel ====

  const handleFileProgreso = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setErrorMessage("");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        setProgresoData(json);
      } catch (err) {
        console.error(err);
        setErrorMessage("Error al leer el archivo de progreso. Verifica el formato.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileCalificaciones = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setErrorMessage("");
    setLoading(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
        setCalificacionesData(json);
      } catch (err) {
        console.error(err);
        setErrorMessage("Error al leer el archivo de calificaciones. Verifica el formato.");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // ==== Unificación de datos cuando ambos Excels están cargados ====
  useEffect(() => {
    if (!progresoData.length || !calificacionesData.length) {
      setAlumnos([]);
      return;
    }

    try {
      const progresosPorNombre = new Map();

      // Mapeamos: "Nombre completo" -> datos de progreso
      progresoData.forEach((row) => {
        const nombreCompleto = String(row["Nombre completo"] || "").trim();
        const key = normalizeName(nombreCompleto);
        if (!key) return;

        const progreso = parseProgress(row["Progreso"]);
        const ultimoEnCurso = row["Último en el curso"] || "";

        progresosPorNombre.set(key, {
          progreso,
          ultimoEnCurso,
        });
      });

      const totalsToSkip = [
        "Total Actividad Diagnóstica (Real)",
        "Total Actividad Formativa (Real)",
        "Total Actividad Integradora (Real)",
        "Total Producto Final (Real)",
        "Total Autoevaluación (Real)",
        "Total del curso (Real)",
      ];

      // PRIMER PASO: detectar qué columnas (Real) tienen números en algún alumno
      const columnHasNumeric = {};

      calificacionesData.forEach((row) => {
        Object.entries(row).forEach(([colName, value]) => {
          if (!colName.includes("(Real)")) return;
          if (totalsToSkip.includes(colName)) return;

          // Ignorar columnas duplicadas tipo "(Real).1", "(Real).2", etc.
          const duplicatedColumnRegex = /\(Real\)\.\d+$/;
          if (duplicatedColumnRegex.test(colName)) return;

          const strVal = String(value).trim();
          if (strVal === "" || strVal === "-" || strVal.toLowerCase() === "na")
            return;

          const grade = parseFloat(strVal.replace(",", "."));
          if (!isNaN(grade)) {
            columnHasNumeric[colName] = true;
          }
        });
      });

      const newAlumnos = [];
      const now = new Date();

      // SEGUNDO PASO: construir estructura de alumnos usando solo columnas con números
      calificacionesData.forEach((row) => {
        const nombre = String(row["Nombre"] || "").trim();
        const apellidos = String(row["Apellido(s)"] || "").trim();
        let nombreCompleto = `${nombre} ${apellidos}`.trim();

        if (!nombreCompleto) {
          nombreCompleto =
            String(
              row["Nombre completo"] ||
                row["Alumno"] ||
                row["Estudiante"] ||
                ""
            ).trim();
        }

        const key = normalizeName(nombreCompleto);
        if (!key) return;

        const numeroId = row["Número de ID"] || row["ID"] || "";
        const email = row["Dirección Email"] || row["Email"] || "";

        const totalCurso =
          parseFloat(
            String(row["Total del curso (Real)"] || "")
              .replace(",", ".")
              .trim()
          ) || 0;

        const totalDiagnostica =
          parseFloat(
            String(row["Total Actividad Diagnóstica (Real)"] || "")
              .replace(",", ".")
              .trim()
          ) || 0;
        const totalFormativa =
          parseFloat(
            String(row["Total Actividad Formativa (Real)"] || "")
              .replace(",", ".")
              .trim()
          ) || 0;
        const totalIntegradora =
          parseFloat(
            String(row["Total Actividad Integradora (Real)"] || "")
              .replace(",", ".")
              .trim()
          ) || 0;
        const totalProductoFinal =
          parseFloat(
            String(row["Total Producto Final (Real)"] || "")
              .replace(",", ".")
              .trim()
          ) || 0;

        const actividades = [];

        Object.entries(row).forEach(([colName, value]) => {
          if (!colName.includes("(Real)")) return;
          if (totalsToSkip.includes(colName)) return;

          const duplicatedColumnRegex = /\(Real\)\.\d+$/;
          if (duplicatedColumnRegex.test(colName)) return;

          // Solo usar columnas que en algún alumno tuvieron un número
          if (!columnHasNumeric[colName]) return;

          const strVal = String(value).trim();
          const grade = parseFloat(strVal.replace(",", "."));
          const calificacion = isNaN(grade) ? null : grade;

          // 🔴 NUEVA REGLA:
          // Entregado solo si hay calificación numérica > 0
          const entregado =
            calificacion != null && typeof calificacion === "number" && calificacion > 0;

          const tipo = inferActivityType(colName);
          const unidad = inferUnidadFromColName(colName);

          actividades.push({
            nombreActividad: colName,
            tipo,
            unidad,
            calificacion,
            entregado,
          });
        });

        const actividadesNoEntregadas = actividades.filter(
          (a) => !a.entregado
        ).length;

        // Resumen por unidad
        const resumenUnidades = {};
        actividades.forEach((act) => {
          const unidad = act.unidad || "General";
          if (!resumenUnidades[unidad]) {
            resumenUnidades[unidad] = {
              unidad,
              totalActividades: 0,
              completadas: 0,
              forosTotales: 0,
              forosCompletadas: 0,
              tareasTotales: 0,
              tareasCompletadas: 0,
              integradorasTotales: 0,
              integradorasCompletadas: 0,
              productoFinalTotales: 0,
              productoFinalCompletadas: 0,
            };
          }

          const res = resumenUnidades[unidad];
          res.totalActividades += 1;
          if (act.entregado) {
            res.completadas += 1;
          }

          if (act.tipo === "Foro") {
            res.forosTotales += 1;
            if (act.entregado) res.forosCompletadas += 1;
          } else if (act.tipo === "Tarea") {
            res.tareasTotales += 1;
            if (act.entregado) res.tareasCompletadas += 1;
          } else if (act.tipo === "Integradora") {
            res.integradorasTotales += 1;
            if (act.entregado) res.integradorasCompletadas += 1;
          } else if (act.tipo === "Producto final") {
            res.productoFinalTotales += 1;
            if (act.entregado) res.productoFinalCompletadas += 1;
          }
        });

        const progresoInfo = progresosPorNombre.get(key);
        const progreso = progresoInfo?.progreso ?? 0;
        const ultimoEnCurso = progresoInfo?.ultimoEnCurso || "";

        let diasDesdeUltimaConexion = null;
        if (ultimoEnCurso) {
          const lastDate = parseMoodleDate(ultimoEnCurso);
          if (lastDate) {
            diasDesdeUltimaConexion = diffDays(now, lastDate);
          }
        }

        const riesgo = calculateRisk({
          totalCurso,
          progreso,
          diasDesdeUltimaConexion,
          actividadesNoEntregadas,
        });

        newAlumnos.push({
          id: numeroId || key,
          nombreCompleto,
          numeroId,
          email,
          progreso,
          ultimoEnCurso,
          diasDesdeUltimaConexion,
          totalCurso,
          totalDiagnostica,
          totalFormativa,
          totalIntegradora,
          totalProductoFinal,
          actividades,
          actividadesNoEntregadas,
          resumenUnidades,
          riesgo,
        });
      });

      setAlumnos(newAlumnos);
      setErrorMessage("");
    } catch (err) {
      console.error(err);
      setErrorMessage("Ocurrió un error al procesar los datos combinados.");
      setAlumnos([]);
    }
  }, [progresoData, calificacionesData]);

  // ==== Métricas rápidas ====
  const totalAlumnos = alumnos.length;
  const riesgoBajo = alumnos.filter((a) => a.riesgo === "bajo").length;
  const riesgoMedio = alumnos.filter((a) => a.riesgo === "medio").length;
  const riesgoAlto = alumnos.filter((a) => a.riesgo === "alto").length;
  const riesgoMuyAlto = alumnos.filter((a) => a.riesgo === "muy_alto").length;
  const sinConexion14 = alumnos.filter(
    (a) =>
      a.diasDesdeUltimaConexion != null && a.diasDesdeUltimaConexion > 14
  ).length;

  // ==== Templates para DataTable ====

  const riesgoBodyTemplate = (rowData) => (
    <Tag
      value={riskToLabel(rowData.riesgo)}
      severity={riskToSeverity(rowData.riesgo)}
    />
  );

  const progresoBodyTemplate = (rowData) => (
    <span>{rowData.progreso.toFixed(1)}%</span>
  );

  const totalCursoBodyTemplate = (rowData) => (
    <span>{rowData.totalCurso.toFixed(1)}</span>
  );

  const ultimoEnCursoBodyTemplate = (rowData) => (
    <span>{rowData.ultimoEnCurso || "Sin registro"}</span>
  );

  const detalleBodyTemplate = (rowData) => (
    <Button
      label="Detalle"
      size="small"
      onClick={() => setSelectedAlumno(rowData)}
    />
  );

  // ==== Vista detalle de alumno ====
  const renderDetalleDialog = () => {
    if (!selectedAlumno) return null;

    const a = selectedAlumno;
    const unidades = Object.values(a.resumenUnidades || {});

    unidades.sort((u1, u2) => u1.unidad.localeCompare(u2.unidad, "es"));

    return (
      <Dialog
        header={`Detalle de ${a.nombreCompleto}`}
        visible={!!selectedAlumno}
        style={{ width: "80vw", maxWidth: 900 }}
        modal
        onHide={() => setSelectedAlumno(null)}
      >
        <div style={{ marginBottom: "1.5rem" }}>
          <p>
            <strong>Nombre:</strong> {a.nombreCompleto}
          </p>
          {a.numeroId && (
            <p>
              <strong>ID:</strong> {a.numeroId}
            </p>
          )}
          {a.email && (
            <p>
              <strong>Correo:</strong> {a.email}
            </p>
          )}
          <p>
            <strong>Progreso:</strong> {a.progreso.toFixed(1)}%
          </p>
          <p>
            <strong>Última conexión:</strong>{" "}
            {a.ultimoEnCurso || "Sin registro"}
            {a.diasDesdeUltimaConexion != null &&
              ` (${a.diasDesdeUltimaConexion} días)`}
          </p>
          <p>
            <strong>Calificación total del curso:</strong>{" "}
            {a.totalCurso.toFixed(1)}
          </p>
          <p>
            <strong>Riesgo:</strong>{" "}
            <Tag
              value={riskToLabel(a.riesgo)}
              severity={riskToSeverity(a.riesgo)}
            />
          </p>
          <p>
            <strong>Actividades no entregadas:</strong>{" "}
            {a.actividadesNoEntregadas}
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            <div
              style={{
                padding: "0.75rem 1rem",
                background: "#ffffff",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <strong>Diagnósticas:</strong>{" "}
              {a.totalDiagnostica.toFixed(1)}
            </div>
            <div
              style={{
                padding: "0.75rem 1rem",
                background: "#ffffff",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <strong>Formativas:</strong> {a.totalFormativa.toFixed(1)}
            </div>
            <div
              style={{
                padding: "0.75rem 1rem",
                background: "#ffffff",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <strong>Integradoras:</strong>{" "}
              {a.totalIntegradora.toFixed(1)}
            </div>
            <div
              style={{
                padding: "0.75rem 1rem",
                background: "#ffffff",
                borderRadius: 8,
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <strong>Producto final:</strong>{" "}
              {a.totalProductoFinal.toFixed(1)}
            </div>
          </div>
        </div>

        <h3 style={{ marginBottom: "0.5rem", color: "#0f3b63" }}>
          Resumen por unidad
        </h3>
        {unidades.length === 0 ? (
          <p style={{ fontSize: "0.9rem" }}>
            No se detectaron unidades en las actividades.
          </p>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            {unidades.map((u) => (
              <div
                key={u.unidad}
                style={{
                  background: "#ffffff",
                  padding: "0.75rem 1rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 5px rgba(0,0,0,0.08)",
                  fontSize: "0.85rem",
                }}
              >
                <p
                  style={{
                    marginTop: 0,
                    marginBottom: "0.5rem",
                    fontWeight: 700,
                    color: "#0f3b63",
                  }}
                >
                  {u.unidad}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Actividades:</strong> {u.completadas}/
                  {u.totalActividades}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Foros:</strong> {u.forosCompletadas}/
                  {u.forosTotales}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Tareas:</strong> {u.tareasCompletadas}/
                  {u.tareasTotales}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Integradoras:</strong>{" "}
                  {u.integradorasCompletadas}/{u.integradorasTotales}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Producto final:</strong>{" "}
                  {u.productoFinalCompletadas}/{u.productoFinalTotales}
                </p>
              </div>
            ))}
          </div>
        )}

        <h3 style={{ marginBottom: "0.75rem", color: "#0f3b63" }}>
          Actividades
        </h3>
        <DataTable
          value={a.actividades}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 20, 50]}
          style={{ fontSize: "0.9rem" }}
        >
          <Column
            field="nombreActividad"
            header="Actividad"
            sortable
            style={{ minWidth: "18rem" }}
          />
          <Column field="unidad" header="Unidad" sortable />
          <Column field="tipo" header="Tipo" sortable />
          <Column
            field="calificacion"
            header="Calificación"
            body={(row) =>
              row.calificacion == null ? "-" : row.calificacion.toFixed(1)
            }
          />
          <Column
            header="Estado"
            body={(row) =>
              row.entregado ? (
                <Tag value="Entregado" severity="success" />
              ) : (
                <Tag value="No entregado" severity="danger" />
              )
            }
          />
        </DataTable>
      </Dialog>
    );
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <Navbar />

      <main style={{ padding: "2rem" }}>
        <h1 style={{ fontWeight: 800, color: "#0f3b63", marginBottom: "1rem" }}>
          Bienvenido a la herramienta para profesores de la carrera de
          Marketing Digital
        </h1>

        <p style={{ maxWidth: 700, lineHeight: 1.6, marginBottom: "1.5rem" }}>
          Aquí solo deberás subir los documentos de{" "}
          <strong>progreso del curso</strong> y{" "}
          <strong>calificaciones de actividades</strong> exportados desde
          Moodle. La herramienta te mostrará un resumen del avance del grupo,
          los riesgos de reprobación y el detalle por unidad de cada alumno.
        </p>

        {/* Zona de carga de archivos */}
        <section
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: "1.5rem",
            marginBottom: "2rem",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              padding: "1.5rem",
              borderRadius: 12,
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: "0.75rem",
                color: "#0f3b63",
                fontSize: "1.1rem",
              }}
            >
              1. Subir reporte de progreso
            </h2>
            <p style={{ fontSize: "0.9rem", marginBottom: "0.75rem" }}>
              Archivo con columnas como{" "}
              <strong>Nombre completo, Último en el curso, Progreso</strong>.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileProgreso}
            />
          </div>

          <div
            style={{
              background: "#ffffff",
              padding: "1.5rem",
              borderRadius: 12,
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            }}
          >
            <h2
              style={{
                marginTop: 0,
                marginBottom: "0.75rem",
                color: "#0f3b63",
                fontSize: "1.1rem",
              }}
            >
              2. Subir reporte de calificaciones
            </h2>
            <p style={{ fontSize: "0.9rem", marginBottom: "0.75rem" }}>
              Archivo con <strong>calificaciones por actividad</strong> y{" "}
              <strong>Total del curso (Real)</strong>.
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileCalificaciones}
            />
          </div>
        </section>

        {/* Mensajes de estado */}
        {loading && (
          <p style={{ color: "#0f3b63", marginBottom: "1rem" }}>
            Procesando archivos...
          </p>
        )}
        {errorMessage && (
          <p style={{ color: "#b00020", marginBottom: "1rem" }}>
            {errorMessage}
          </p>
        )}

        {/* Resumen y tabla solo si hay alumnos */}
        {alumnos.length > 0 && (
          <>
            {/* Tarjetas de resumen */}
            <section
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
                gap: "1rem",
                marginBottom: "1.5rem",
              }}
            >
              <div
                style={{
                  background: "#ffffff",
                  padding: "1rem 1.25rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#555",
                  }}
                >
                  Total de alumnos
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#0f3b63",
                  }}
                >
                  {totalAlumnos}
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "1rem 1.25rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#555",
                  }}
                >
                  Riesgo muy alto
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#d32f2f",
                  }}
                >
                  {riesgoMuyAlto}
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "1rem 1.25rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#555",
                  }}
                >
                  Riesgo alto
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#f57c00",
                  }}
                >
                  {riesgoAlto}
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "1rem 1.25rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#555",
                  }}
                >
                  Riesgo medio
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#ffa000",
                  }}
                >
                  {riesgoMedio}
                </p>
              </div>

              <div
                style={{
                  background: "#ffffff",
                  padding: "1rem 1.25rem",
                  borderRadius: 10,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "0.85rem",
                    color: "#555",
                  }}
                >
                  Sin conexión &gt; 14 días
                </p>
                <p
                  style={{
                    margin: 0,
                    fontSize: "1.4rem",
                    fontWeight: 700,
                    color: "#0f3b63",
                  }}
                >
                  {sinConexion14}
                </p>
              </div>
            </section>

            {/* Tabla principal */}
            <section>
              <h2
                style={{
                  color: "#0f3b63",
                  fontSize: "1.3rem",
                  marginBottom: "0.75rem",
                }}
              >
                Resumen del grupo
              </h2>
              <DataTable
                value={alumnos}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 20, 50]}
                sortMode="multiple"
                style={{ background: "#ffffff", borderRadius: 10 }}
                stripedRows
              >
                <Column
                  field="nombreCompleto"
                  header="Alumno"
                  sortable
                  style={{ minWidth: "18rem" }}
                />
                <Column
                  header="Progreso"
                  body={progresoBodyTemplate}
                  sortable
                  style={{ minWidth: "7rem" }}
                />
                <Column
                  header="Total curso"
                  body={totalCursoBodyTemplate}
                  sortable
                  style={{ minWidth: "7rem" }}
                />
                <Column
                  header="Última conexión"
                  body={ultimoEnCursoBodyTemplate}
                  style={{ minWidth: "16rem" }}
                />
                <Column
                  header="Riesgo"
                  body={riesgoBodyTemplate}
                  sortable
                  style={{ minWidth: "7rem" }}
                />
                <Column
                  header="Detalle"
                  body={detalleBodyTemplate}
                  style={{ minWidth: "6rem", textAlign: "center" }}
                />
              </DataTable>
            </section>
          </>
        )}

        {renderDetalleDialog()}
      </main>
    </div>
  );
}
